<template>
  <div class="question-layout mt-2">
    <div class="d-flex">
      <!-- 左: 画像パネル -->
      <div class="image-panel" :class="{ collapsed: !showImagePanel }">
        <div v-if="showImagePanel" class="image-panel-inner">
          <div class="panel-header d-flex align-center pa-2">
            <v-btn-toggle v-model="selectedImageType" mandatory dense class="image-type-toggle">
              <v-btn small value="answer">解答</v-btn>
              <v-btn small value="question">問題</v-btn>
            </v-btn-toggle>
            <v-spacer></v-spacer>
            <v-btn icon x-small @click="showImagePanel = false">
              <v-icon small>mdi-chevron-left</v-icon>
            </v-btn>
          </div>
          <v-divider></v-divider>
          <div class="image-scroll-area">
            <div v-if="currentImages.length" class="pa-2">
              <div
                v-for="(img, idx) in currentImages"
                :key="img.id"
                class="image-item-panel mb-2"
              >
                <div class="image-wrapper" @click="openPreview(img)">
                  <v-img
                    :src="img.expiring_url"
                    contain
                    class="grey lighten-4 rounded"
                  >
                    <template #placeholder>
                      <v-row class="fill-height ma-0" align="center" justify="center">
                        <v-progress-circular indeterminate size="20" color="grey"></v-progress-circular>
                      </v-row>
                    </template>
                    <div class="hover-overlay d-flex align-center justify-center">
                      <v-icon color="white" small>mdi-magnify-plus-outline</v-icon>
                    </div>
                  </v-img>
                </div>
                <div class="text-caption text-truncate text-center mt-1" :title="img.file_name">
                  {{ idx + 1 }}. {{ img.file_name }}
                </div>
              </div>
            </div>
            <div v-else class="pa-4 text-center">
              <v-icon color="grey lighten-1" size="32">mdi-image-off-outline</v-icon>
              <div class="text-caption text--secondary mt-1">画像がありません</div>
            </div>
          </div>
        </div>
        <div v-else class="collapsed-tab" @click="showImagePanel = true">
          <v-icon small class="mb-1">mdi-image-outline</v-icon>
          <span class="collapsed-label">画像</span>
        </div>
      </div>

      <!-- 右: 設問テーブル -->
      <div class="table-panel flex-grow-1">
        <v-data-table
          :headers="headers"
          :items="questions"
          hide-default-footer
          :items-per-page="-1"
          dense
        >
          <template #[`item.point_name`]="{ item }">
            {{ item.point_short_name || item.point_name }}
          </template>
          <template #[`item.edit`]="{ item }">
            <v-btn icon @click.stop="openEditDialog(item)">
              <v-icon small dark>mdi-pencil</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </div>
    </div>

    <!-- 画像拡大モーダル -->
    <v-dialog v-model="previewDialog" max-width="900">
      <v-card v-if="previewImage">
        <v-toolbar flat dense>
          <v-toolbar-title class="text-body-2">{{ previewImage.file_name }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="previewDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-divider></v-divider>
        <div class="preview-body pa-4 text-center">
          <v-img
            :src="previewImage.expiring_url"
            max-height="70vh"
            contain
          >
            <template #placeholder>
              <v-row class="fill-height ma-0" align="center" justify="center">
                <v-progress-circular indeterminate size="40" color="secondary"></v-progress-circular>
              </v-row>
            </template>
          </v-img>
        </div>
      </v-card>
    </v-dialog>

    <!-- 設問編集ダイアログ -->
    <v-dialog v-model="editDialog" persistent content-class="edit-dialog-fullwidth">
      <v-card class="edit-dialog-card">
        <v-card-title class="py-2 px-4">
          <span class="text-subtitle-1 font-weight-bold">設問編集 ({{ editedQuestion.number }}-{{ editedQuestion.sub_number }})</span>
          <v-spacer></v-spacer>
          <v-btn icon small @click="editDialog = false"><v-icon small>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <div class="d-flex edit-dialog-body">
          <!-- 左: 画像（広い） -->
          <div class="edit-image-panel">
            <div class="d-flex align-center pa-2">
              <v-btn-toggle v-model="editImageType" mandatory dense class="edit-image-toggle">
                <v-btn small value="answer">解答</v-btn>
                <v-btn small value="question">問題</v-btn>
              </v-btn-toggle>
              <v-spacer></v-spacer>
              <span class="text-caption text--secondary">{{ editImages.length }}枚</span>
            </div>
            <v-divider></v-divider>
            <div class="edit-image-scroll">
              <div v-if="editImages.length" class="pa-3">
                <div
                  v-for="(img, idx) in editImages"
                  :key="img.id"
                  class="edit-image-item mb-3"
                >
                  <div class="edit-image-thumb" @click="openPreview(img)">
                    <v-img
                      :src="img.expiring_url"
                      max-height="360"
                      contain
                      class="grey lighten-4 rounded"
                    >
                      <template #placeholder>
                        <v-row class="fill-height ma-0" align="center" justify="center">
                          <v-progress-circular indeterminate size="24" color="grey"></v-progress-circular>
                        </v-row>
                      </template>
                      <div class="hover-overlay d-flex align-center justify-center">
                        <v-icon color="white">mdi-magnify-plus-outline</v-icon>
                      </div>
                    </v-img>
                  </div>
                  <div class="text-caption text-truncate text-center mt-1" :title="img.file_name">{{ idx + 1 }}. {{ img.file_name }}</div>
                </div>
              </div>
              <div v-else class="pa-8 text-center">
                <v-icon color="grey lighten-1" size="48">mdi-image-off-outline</v-icon>
                <div class="text--secondary mt-2">画像がありません</div>
              </div>
            </div>
          </div>

          <!-- 右: フォーム（コンパクト縦並び） -->
          <div class="edit-form-panel">
            <div class="edit-form-inner pa-4">
              <v-text-field v-model="editedQuestion.answer" label="解答" outlined dense class="mb-3"></v-text-field>
              <v-select
                v-model="editedQuestion.point_id"
                :items="points"
                item-text="name"
                item-value="id"
                label="観点"
                outlined
                dense
                class="mb-3"
              ></v-select>
              <v-select
                v-model="editedQuestion.question_type_id"
                :items="questionTypes"
                item-text="name"
                item-value="id"
                label="問題形式"
                outlined
                dense
                class="mb-3"
              ></v-select>
              <v-select
                v-model="editedQuestion.answer_format_id"
                :items="answerFormats"
                item-text="name"
                item-value="id"
                label="解答形式"
                outlined
                dense
                class="mb-3"
              ></v-select>
              <v-text-field
                v-model="editedQuestion.question_cnt"
                label="小問数"
                type="number"
                outlined
                dense
              ></v-text-field>
            </div>
            <v-divider></v-divider>
            <div class="d-flex pa-3">
              <v-spacer></v-spacer>
              <v-btn text @click="editDialog = false">キャンセル</v-btn>
              <v-btn color="secondary" dark class="ml-2" @click="saveQuestion">
                <v-icon left small>mdi-content-save</v-icon>
                保存
              </v-btn>
            </div>
          </div>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  props: {
    questions: { type: Array, default: () => [] },
    contentImages: { type: Object, default: () => ({}) },
    contentId: { type: Number, required: true },
  },
  data() {
    return {
      headers: [
        { text: '問番号', align: 'center', value: 'number' },
        { text: '枝問番号', align: 'center', value: 'sub_number' },
        { text: '解答', align: 'start', value: 'answer' },
        { text: '観点', align: 'center', value: 'point_name' },
        { text: '問題形式', align: 'center', value: 'question_type_name' },
        { text: '解答形式', align: 'center', value: 'answer_format_name' },
        { text: '小問数', align: 'center', value: 'question_cnt' },
        { text: '編集', align: 'center', value: 'edit', sortable: false },
      ],
      editDialog: false,
      editedQuestion: {},
      showImagePanel: true,
      selectedImageType: 'answer',
      editImageType: 'answer',
      previewDialog: false,
      previewImage: null,
    }
  },
  computed: {
    points() { return this.$store.state.master.allPoints || [] },
    questionTypes() { return this.$store.state.master.allQuestionTypes || [] },
    answerFormats() { return this.$store.state.master.allAnswerFormats || [] },
    currentImages() {
      return this.contentImages[this.selectedImageType] || []
    },
    editImages() {
      return this.contentImages[this.editImageType] || []
    },
  },
  methods: {
    openPreview(img) {
      this.previewImage = img
      this.previewDialog = true
    },
    openEditDialog(question) {
      this.editedQuestion = { ...question }
      this.editImageType = 'answer'
      this.editDialog = true
    },
    async saveQuestion() {
      const url = `/api/v1/admin/contents/${this.contentId}/questions/${this.editedQuestion.id}`
      try {
        await this.$axios.put(url, {
          question: {
            answer: this.editedQuestion.answer,
            point_id: this.editedQuestion.point_id,
            question_type_id: this.editedQuestion.question_type_id,
            answer_format_id: this.editedQuestion.answer_format_id,
            question_cnt: this.editedQuestion.question_cnt,
          }
        })
        this.editDialog = false
        this.$toasted.global.success({ message: '設問を更新しました' })
        this.$emit('updated')
      } catch (e) {
        this.$toasted.global.error({ message: '設問の更新に失敗しました' })
      }
    },
  },
}
</script>

<style lang="stylus" scoped>
.question-layout
  overflow hidden

.image-panel
  flex-shrink 0
  width 280px
  border-right 1px solid #e0e0e0
  background #fafafa
  transition width 0.2s ease
  display flex

  &.collapsed
    width 36px

.image-panel-inner
  width 280px
  display flex
  flex-direction column
  height calc(100vh - 280px)
  min-height 400px

.panel-header
  background #fff

.image-type-toggle
  .v-btn
    text-transform none
    letter-spacing 0

.image-scroll-area
  overflow-y auto
  flex 1

.image-item-panel
  .image-wrapper
    cursor pointer
    position relative
    border-radius 4px
    overflow hidden

  .hover-overlay
    position absolute
    top 0
    left 0
    right 0
    bottom 0
    background rgba(0, 0, 0, 0.3)
    opacity 0
    transition opacity 0.2s ease

  .image-wrapper:hover .hover-overlay
    opacity 1

.collapsed-tab
  writing-mode vertical-rl
  display flex
  align-items center
  justify-content center
  gap 4px
  width 36px
  cursor pointer
  color #666
  font-size 12px
  padding 12px 0
  user-select none

  &:hover
    background #f0f0f0

.collapsed-label
  letter-spacing 2px

.table-panel
  overflow auto
  min-width 0

.preview-body
  background #f5f5f5

.edit-dialog-body
  height calc(90vh - 52px)

.edit-image-panel
  flex 1 1 0
  min-width 0
  max-width 700px
  border-right 1px solid #e0e0e0
  background #fafafa
  display flex
  flex-direction column

.edit-image-toggle
  .v-btn
    text-transform none
    letter-spacing 0

.edit-image-scroll
  overflow-y auto
  flex 1

.edit-image-item
  .edit-image-thumb
    cursor pointer
    position relative
    border-radius 4px
    overflow hidden

  .hover-overlay
    position absolute
    top 0
    left 0
    right 0
    bottom 0
    background rgba(0, 0, 0, 0.3)
    opacity 0
    transition opacity 0.2s ease

  .edit-image-thumb:hover .hover-overlay
    opacity 1

.edit-form-panel
  width 300px
  flex-shrink 0
  display flex
  flex-direction column

.edit-form-inner
  flex 1
  overflow-y auto
</style>

<style lang="stylus">
.edit-dialog-fullwidth
  width 1040px !important
  max-width calc(100vw - 40px) !important
  margin 20px auto

  .edit-dialog-card
    height 90vh
    display flex
    flex-direction column
    overflow hidden

    > .v-card__title
      flex-shrink 0

    > .edit-dialog-body
      flex 1
      min-height 0
</style>

